import { isString } from '../utility/helper';
import { AvailableExperiment } from '../utility/constants/ab-testing';

export type TLink = {
  id: string;
  text: string;
  link: string;
  icon: string;
  description: string;
};

export type TButton = {
  id: string;
  text: string;
  description: string;
};

export type TMenu = {
  id: string;
  text: string;
  link: string;
  children: TMenu[];
  experienceName?: AvailableExperiment;
};

export type TShopAuthStatus = 'loggedOut' | 'loggedIn' | null;

export type TShopAdditionalContentMenu = {
  id: string;
  link: string;
  text: string;
  iconUrl?: string;
  icon?: string;
  authStatus?: TShopAuthStatus;
};

export type TShopMenu = {
  id: string;
  text: string;
  link: string;
  cart?: string;
  children: TMenu[];
  additionalContent: TShopAdditionalContentMenu[];
  availableLocales: string[];
  availableLocalesOnlyForShop: string[];
  logoLinksToShopLocales: string[];
};

export type ShopContentResponse = {
  menuItems: TMenu[];
  additionalContent: TShopAdditionalContentMenu[];
};

export type TVehiclePurchaseMenu = {
  id: string;
  text: string;
  link: string;
  children: TMenu[];
  savedVehicles?: TLink | null;
};

export type TCountryRecommender = {
  statement: string;
  region: string;
  language: string;
  change: TButton;
};

export type Models = {
  text: string;
  id: string;
  all: TLink;
  series: Series[];
};

export type Series = {
  name: string;
  id: string;
  all: TLink;
  image: string;
  links: TLink[];
  tags: Tag[];
  bodyTypes: BodyType[];
};

export type BodyType = {
  id: string;
  name: string;
  description: string;
  image: string;
  link: string;
};

export type Tag = {
  text: string;
  icon: string;
};

export type Dealer = {
  id: string;
  text: string;
  numberOfDealers: number;
  search: {
    id: string;
    text: string;
    form: {
      inputPlaceholder: string;
      inputDescription: string;
    };
    clear: TButton;
    or: string;
    geolocation: TButton;
    dropdown: {
      province: string;
      city: string;
      selectAProvince: string;
      selectACity: string;
    };
    load: string;
    map: TLink;
    outboundLink: TLink | null;
  };
  details: {
    id: string;
    finder: TLink | null;
    bookService: TLink | null;
    testDrive: TLink | null;
    backToSearch: string;
  };
  openingHours: OpeningHours | null;
};

export type MyPorsche = {
  id: string;
  text: string;
  link: string;
  loggedInFallback: string; //fallback if user has no name
  login: TLink;
  loginText: string;
  logout: TLink;
  messages: TLink;
  more: TLink | null;
  signup: TLink;
  signupText: string;
  links: TLink[];
  savedSearches: TLink | null;
  savedVehicles: TLink | null;
};

export type Errors = {
  wrong: string;
  retry: string;
  noResults: string;
  noInput: string;
  invalidInput: string;
  geolocationDenied: string;
};

export type Content = {
  menu: TButton;
  crest: TLink;
  back: TButton;
  close: TButton;
  accessibilityStatement: TLink | null;
  countryRecommender: TCountryRecommender;
  contextualButtons: {
    [buttonName: string]: TButton;
  };
  contextualLinks: {
    [linkName: string]: TLink;
  };
  models: Models;
  dealer: Dealer;
  myPorsche: MyPorsche | null;
  vehiclePurchase: TMenu;
  shop: TShopMenu | null;

  externalShop: TLink | null;
  experience: TMenu;
  services: TMenu;
  legacyMore: TMenu;
  legacyShop: TMenu;
  error: Errors;
  loadingDescription: string;
  abExperiences?: ContentExperimentDefinition[];
};

export type Target = {
  attribute: string;
  inverted: string;
  type: string;
  value: string;
};

export type VariationDefinition = {
  name: string;
  percentage: number;
  children: TMenu[];
};

export type ContentExperimentDefinition = {
  name: string;
  percentage: number;
  includedTargets: Target[];
  excludedTargets: Target[];
  variations: VariationDefinition[];
};

export type OpeningHours = {
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  daily_lunch: string;
  timeFormat?: number;
};

const EMPTY_LINK: TLink = { id: '', text: '', link: '', icon: '', description: '' };

const EMPTY_BUTTON: TButton = { id: '', text: '', description: '' };

const EMPTY_COUNTRY_RECOMMENDER: TCountryRecommender = {
  statement: '',
  region: '',
  language: '',
  change: EMPTY_BUTTON
};

const EMPTY_MODELS: Models = {
  text: '',
  id: '',
  all: {
    id: '',
    text: '',
    link: '',
    icon: '',
    description: ''
  },
  series: []
};

const EMPTY_MENU: TMenu = {
  id: '',
  text: '',
  link: '',
  children: []
};

export const EMPTY_SHOP_MENU: TShopMenu = {
  cart: '',
  additionalContent: [],
  id: '',
  text: '',
  link: '',
  children: [],
  availableLocales: [],
  availableLocalesOnlyForShop: [],
  logoLinksToShopLocales: []
};

const EMPTY_MYPORSCHE: MyPorsche = {
  id: '',
  text: '',
  link: '',
  loggedInFallback: '', //fallback if user has no name
  login: EMPTY_LINK,
  loginText: '',
  logout: EMPTY_LINK,
  messages: EMPTY_LINK,
  more: null,
  signup: EMPTY_LINK,
  signupText: '',
  links: [],
  savedSearches: null,
  savedVehicles: null
};

const EMPTY_DEALER: Dealer = {
  id: '',
  text: '',
  numberOfDealers: 42,
  search: {
    id: '',
    text: '',
    form: {
      inputPlaceholder: '',
      inputDescription: ''
    },
    clear: EMPTY_BUTTON,
    or: '',
    geolocation: EMPTY_BUTTON,
    dropdown: {
      province: '',
      city: '',
      selectAProvince: '',
      selectACity: ''
    },
    load: '',
    map: EMPTY_LINK,
    outboundLink: EMPTY_LINK
  },
  details: {
    id: '',
    finder: EMPTY_LINK,
    bookService: EMPTY_LINK,
    testDrive: EMPTY_LINK,
    backToSearch: ''
  },
  openingHours: null
};

const EMPTY_ERRORS: Errors = {
  wrong: '',
  retry: '',
  noResults: '',
  noInput: '',
  invalidInput: '',
  geolocationDenied: ''
};

export const EMPTY_CONTENT: Content = {
  menu: EMPTY_BUTTON,
  crest: EMPTY_LINK,
  back: EMPTY_BUTTON,
  close: EMPTY_BUTTON,
  accessibilityStatement: EMPTY_LINK,
  countryRecommender: EMPTY_COUNTRY_RECOMMENDER,
  contextualButtons: {},
  contextualLinks: {},
  models: EMPTY_MODELS,
  dealer: EMPTY_DEALER,
  myPorsche: EMPTY_MYPORSCHE,
  vehiclePurchase: EMPTY_MENU,
  shop: EMPTY_SHOP_MENU,
  externalShop: EMPTY_LINK,
  experience: EMPTY_MENU,
  services: EMPTY_MENU,
  error: EMPTY_ERRORS,
  loadingDescription: '',
  legacyMore: EMPTY_MENU,
  legacyShop: EMPTY_MENU
} as const;

export function findMenuItemById(menu: TMenu | null, id: string): TMenu | null {
  if (!isString(id)) {
    return null;
  }

  if (menu !== null) {
    if (menu.id === id) {
      return menu;
    }

    if (!Array.isArray(menu.children)) {
      return null;
    }

    for (const child of menu.children) {
      const foundChild = findMenuItemById(child, id);

      if (foundChild !== null) {
        return foundChild;
      }
    }
  }

  return null;
}
